/**
 * Product image lightbox on the custom PDP: prev/next arrows and keyboard navigation for
 * Horizon's zoom dialog, which fcd-lightbox.css turns into a one-image-per-screen slideshow.
 *
 * Navigation goes through ZoomDialog.selectThumbnail(), so the thumbnail highlight and the
 * high-res image loading stay Horizon's. Horizon only tracks the visible image from the
 * dialog's own (vertical) scroll; with the gallery now scrolling sideways, a swipe is relayed
 * to its handleScroll so the thumbnails follow.
 */

const ICON_PREV =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_NEXT =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** @param {HTMLElement} zoom */
function currentIndex(zoom) {
  const buttons = [...zoom.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
  const selected = buttons.findIndex((b) => b.getAttribute('aria-selected') === 'true');
  if (selected >= 0) return selected;

  // No thumbnails (single image) or none selected yet: use the slide nearest the centre.
  const gallery = zoom.querySelector('.dialog-zoomed-gallery');
  return gallery && gallery.clientWidth ? Math.round(gallery.scrollLeft / gallery.clientWidth) : 0;
}

/** @param {HTMLElement} zoom */
function slideCount(zoom) {
  return zoom.querySelectorAll('.dialog-zoomed-gallery > .product-media-container').length;
}

/**
 * @param {HTMLElement} zoom
 * @param {number} delta
 */
function go(zoom, delta) {
  const count = slideCount(zoom);
  const target = Math.min(count - 1, Math.max(0, currentIndex(zoom) + delta));
  const component = /** @type {any} */ (zoom);
  if (typeof component.selectThumbnail === 'function') {
    component.selectThumbnail(target, {
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }
  // selectThumbnail returns early when there are no thumbnails; scroll the gallery directly.
  const gallery = zoom.querySelector('.dialog-zoomed-gallery');
  if (gallery && !zoom.querySelector('.dialog-thumbnails-list__thumbnail')) {
    gallery.scrollTo({ left: target * gallery.clientWidth, behavior: 'smooth' });
  }
}

/** @param {HTMLElement} zoom */
function syncArrows(zoom) {
  const count = slideCount(zoom);
  const index = currentIndex(zoom);
  const prev = zoom.querySelector('.fcd-lightbox-arrow--prev');
  const next = zoom.querySelector('.fcd-lightbox-arrow--next');
  if (prev instanceof HTMLButtonElement) prev.disabled = index <= 0;
  if (next instanceof HTMLButtonElement) next.disabled = index >= count - 1;
}

/** @param {HTMLElement} zoom */
function enhance(zoom) {
  if (zoom.dataset.fcdLightbox === 'true') return;
  const dialog = zoom.querySelector('dialog');
  const gallery = /** @type {HTMLElement | null} */ (zoom.querySelector('.dialog-zoomed-gallery'));
  if (!dialog || !gallery) return;
  zoom.dataset.fcdLightbox = 'true';

  if (slideCount(zoom) > 1) {
    for (const { dir, label, icon } of [
      { dir: 'prev', label: 'Previous image', icon: ICON_PREV },
      { dir: 'next', label: 'Next image', icon: ICON_NEXT },
    ]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `fcd-lightbox-arrow fcd-lightbox-arrow--${dir}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = icon;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        go(zoom, dir === 'prev' ? -1 : 1);
      });
      dialog.appendChild(button);
    }
  }

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(zoom, -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(zoom, 1);
    }
  });

  // Touch swipe. Horizon's drag-zoom-wrapper calls preventDefault on every touchstart (for its
  // pinch/drag zoom), which stops the gallery from scrolling under a finger, so native swipe
  // never moved. Read the gesture ourselves and step one image per horizontal swipe.
  let startX = 0;
  let startY = 0;
  let tracking = false;
  gallery.addEventListener(
    'touchstart',
    (event) => {
      tracking = event.touches.length === 1;
      if (!tracking) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    },
    { capture: true, passive: true }
  );
  gallery.addEventListener(
    'touchend',
    (event) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) go(zoom, dx < 0 ? 1 : -1);
    },
    { capture: true, passive: true }
  );

  // Trackpad / scrollbar scroll: let Horizon pick the visible image and highlight its thumbnail.
  let settle = 0;
  gallery.addEventListener(
    'scroll',
    () => {
      clearTimeout(settle);
      settle = window.setTimeout(() => {
        const component = /** @type {any} */ (zoom);
        if (typeof component.handleScroll === 'function') component.handleScroll();
        window.setTimeout(() => syncArrows(zoom), 120);
      }, 80);
    },
    { passive: true }
  );

  // Keep the arrows' disabled state in step with whatever moved the selection.
  new MutationObserver(() => syncArrows(zoom)).observe(zoom, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-selected', 'open'],
  });
  syncArrows(zoom);
}

function init() {
  document.querySelectorAll('.custom-product-left zoom-dialog').forEach((zoom) => enhance(/** @type {HTMLElement} */ (zoom)));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
